<template>
    <div class="container-fluid">
        <!-- Part Search -->
        <part-search-ac @select="onPartSelected"></part-search-ac>

        <!-- Row 1: Client & Request -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Client</span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm" 
                        placeholder="Search for a client..."
                    ></client-ac>
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Request/Job</span>
                    <job-ac 
                        v-model="form.RequestId" 
                        :client="form.CustomerNumber"
                        :pm-only="pmOnly"
                        placeholder="Search for a request..."
                    ></job-ac>
                </label>
            </div>
        </div>

        <!-- Row 2: Assigned To & Order Status -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Assigned To</span>
                    <tech-ac 
                        v-model="form.AssignedTo" 
                        placeholder="Search for a technician..."
                    ></tech-ac>
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <kt-select
                    :options="status"
                    label="Order Status"
                    placeholder="Select Order Status"
                    v-model="form.OrderStatus"
                    inputclass="form-control form-control-sm"
                ></kt-select>
            </div>
        </div>

        <!-- Row 3: Description (full width) -->
        <div class="row">
            <div class="col-12 mb-3">
                <label class="d-block">
                    <span class="form-label">Description</span>
                    <textarea 
                        class="form-control form-control-sm" 
                        rows="4" 
                        v-model="form.PartDescription"
                        placeholder="Part Description"
                    ></textarea>
                </label>
            </div>
        </div>

        <!-- Row 4: Part Number & Link Reference -->
        <div class="row">
            <div class="col-md-8 mb-3">
                <label class="d-block">
                    <span class="form-label">Part Number</span>
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="form.PartNumber" 
                        placeholder="Part Number"
                    >
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Quantity</span>
                    <input 
                        type="number" 
                        class="form-control form-control-sm" 
                        v-model="form.Quantity" 
                        placeholder="Quantity"
                    >
                </label>
            </div>
        </div>

        <!-- Row 5: Pricing -->
        <div class="row">
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">MSRP</span>
                    <input 
                        type="number" 
                        step="0.01" 
                        class="form-control form-control-sm" 
                        v-model="form.MSRP" 
                        placeholder="0.00"
                    >
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Cost</span>
                    <input 
                        type="number" 
                        step="0.01" 
                        class="form-control form-control-sm" 
                        v-model="form.PartsCost" 
                        placeholder="0.00"
                    >
                </label>
            </div>
            <div class="col-md-4 mb-3">
                <label class="d-block">
                    <span class="form-label">Needed By</span>
                    <input 
                        type="date" 
                        class="form-control form-control-sm" 
                        v-model="form.NeededBy"
                    >
                </label>
            </div>
        </div>

        <!-- Row 6: Order Details -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Order Number</span>
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="form.OrderNumber" 
                        placeholder="Order Number"
                    >
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <kt-select
                    :options="payments"
                    label="Payment Method"
                    placeholder="Select Payment Method"
                    v-model="form.PaymentMethod"
                    inputclass="form-control form-control-sm"
                ></kt-select>
            </div>
        </div>

        <!-- Row 7: Link Reference & Work Complete Date -->
        <div class="row">
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Link Reference</span>
                    <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="form.LinkReference" 
                        placeholder="Part Link/URL"
                    >
                </label>
            </div>
            <div class="col-md-6 mb-3">
                <label class="d-block">
                    <span class="form-label">Work Complete Date</span>
                    <input 
                        type="date" 
                        class="form-control form-control-sm" 
                        v-model="form.WorkCompleteDate"
                    >
                </label>
            </div>
        </div>
    </div>
</template>

<script setup>

import { ref } from "vue";
import { PartModel } from "@/models/PartModel";
import Helper, { DateFormat } from "@/services/Helper";

import attic from "@/services/Attic.js";
import KtSelect from "@/components/UI/KtSelect.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import TechAc from "@/components/Techs/TechAc.vue";
import PartSearchAc from "@/components/Parts/PartSearchAc.vue";

const status = ref(attic.OrderStatus);
const payments = ref(attic.Payments);

const props = defineProps({
    pmOnly: {
        type: Boolean,
        default: false
    }
});

const form = defineModel(
{
    type: PartModel,
    default: new PartModel()
});

const onPartSelected = (part) => {
    // Auto-populate form fields from selected part
    form.value.PartDescription = part.PartDescription;
    form.value.PartNumber = part.PartNumber;
    form.value.MSRP = part.MSRP;
    form.value.PartsCost = part.PartsCost;
    form.value.Quantity = part.Quantity || 1;
    form.value.LinkReference = part.LinkReference;
    
    // Set NeededBy and WorkCompleteDate to today
    const today = new Date();
    form.value.NeededBy = Helper.dateToInput(today, DateFormat.Date);
    form.value.WorkCompleteDate = Helper.dateToInput(today, DateFormat.Date);
};

</script>